import React, { useState, useEffect, useRef } from 'react';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { LexRuntimeV2Client, RecognizeUtteranceCommand } from '@aws-sdk/client-lex-runtime-v2';

// Use environment variables directly
const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID || '';
const botId = process.env.REACT_APP_LEX_BOT_ID || '';
const botAliasId = process.env.REACT_APP_LEX_BOT_ALIAS_ID || '';
const localeId = process.env.REACT_APP_LEX_LOCALE_ID || 'en_US';

/**
 * LexDebugger component for testing and debugging Lex voice integration
 */
const LexDebugger: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [lexResponse, setLexResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [decodedData, setDecodedData] = useState<{[key: string]: any}>({});
  const [extractedTranscript, setExtractedTranscript] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const lexClientRef = useRef<LexRuntimeV2Client | null>(null);
  
  // Initialize Lex client
  useEffect(() => {
    const initLexClient = async () => {
      try {
        console.log('LexDebugger: Initializing Lex client...');
        
        // Set up Cognito Identity credentials
        const cognitoIdentityClient = new CognitoIdentityClient({
          region
        });
        
        const credentials = fromCognitoIdentityPool({
          client: cognitoIdentityClient,
          identityPoolId
        });
        
        // Create Lex client
        lexClientRef.current = new LexRuntimeV2Client({
          region,
          credentials,
          maxAttempts: 3,
          retryMode: 'standard'
        });
        
        console.log('LexDebugger: Lex client initialized successfully');
      } catch (err) {
        console.error('LexDebugger: Error initializing Lex client:', err);
        setError('Failed to initialize Lex client');
      }
    };
    
    initLexClient();
  }, []);
  
  // Start recording
  const startRecording = async () => {
    try {
      setError(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setLexResponse(null);
      setDecodedData({});
      setExtractedTranscript(null);
      
      // Check supported MIME types
      const supportedMimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/wav',
        'audio/mp4'
      ].filter(mimeType => MediaRecorder.isTypeSupported(mimeType));
      
      console.log('LexDebugger: Supported MIME types:', supportedMimeTypes);
      
      if (supportedMimeTypes.length === 0) {
        throw new Error('No supported audio MIME types found');
      }
      
      // Use the first supported MIME type
      const mimeType = supportedMimeTypes[0];
      console.log('LexDebugger: Using MIME type:', mimeType);
      
      // Get audio stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 16000
      });
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Combine audio chunks into a single blob
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('LexDebugger: Recorded audio blob:', blob.size, 'bytes, type:', blob.type);
        
        // Create a URL for the audio blob
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        
        // Stop all tracks in the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (err) {
      console.error('LexDebugger: Error starting recording:', err);
      if (err instanceof Error) {
        setError(`Failed to start recording: ${err.message}`);
      } else {
        setError('Failed to start recording');
      }
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  // Send audio to Lex
  const sendToLex = async () => {
    if (!lexClientRef.current) {
      setError('Lex client not initialized');
      return;
    }
    
    if (!audioBlob) {
      setError('No audio recorded');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    setLexResponse(null);
    setDecodedData({});
    setExtractedTranscript(null);
    
    try {
      console.log('LexDebugger: Converting audio to PCM format...');
      
      // Convert audio to PCM format
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      const audioArrayBuffer = await audioBlob.arrayBuffer();
      
      const audioBuffer = await audioContext.decodeAudioData(audioArrayBuffer);
      console.log('LexDebugger: Audio decoded successfully. Duration:', audioBuffer.duration, 'seconds');
      console.log('LexDebugger: Sample rate:', audioBuffer.sampleRate, 'Hz');
      console.log('LexDebugger: Channels:', audioBuffer.numberOfChannels);
      
      // Convert to 16kHz mono PCM
      const pcmData = convertToPCM(audioBuffer, 16000);
      console.log('LexDebugger: PCM data size:', pcmData.byteLength, 'bytes');
      
      // Create a session ID
      const sessionId = `debug-session-${Date.now()}`;
      
      // Create the command
      const command = new RecognizeUtteranceCommand({
        botId,
        botAliasId,
        localeId,
        sessionId: sessionId,
        requestContentType: 'audio/x-l16; sample-rate=16000; channel-count=1',
        inputStream: new Uint8Array(pcmData)
      });
      
      // Send the command
      console.log('LexDebugger: Sending audio to Lex...');
      const response = await lexClientRef.current.send(command);
      console.log('LexDebugger: Received response from Lex:', response);
      
      // Store the response
      setLexResponse(response);
      
      // Process the response
      await processLexResponse(response);
    } catch (err) {
      console.error('LexDebugger: Error sending audio to Lex:', err);
      if (err instanceof Error) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Unknown error occurred');
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Process Lex response
  const processLexResponse = async (response: any) => {
    try {
      // Import the LexResponseDecoder utility
      const { default: LexResponseDecoder } = await import('../utils/LexResponseDecoder');
      
      // Analyze the response
      LexResponseDecoder.analyzeLexResponse(response);
      
      // Extract transcript
      const transcript = LexResponseDecoder.extractTranscriptFromLexResponse(response);
      setExtractedTranscript(transcript);
      
      // Process each field
      const decoded: {[key: string]: any} = {};
      
      // Process inputTranscript
      if (response.inputTranscript) {
        decoded.inputTranscript = LexResponseDecoder.decodeBase64String(response.inputTranscript);
      }
      
      // Process interpretations
      if (response.interpretations) {
        decoded.interpretations = LexResponseDecoder.decodeBase64String(response.interpretations);
      }
      
      // Process sessionState
      if (response.sessionState) {
        decoded.sessionState = LexResponseDecoder.decodeBase64String(response.sessionState);
      }
      
      setDecodedData(decoded);
    } catch (err) {
      console.error('LexDebugger: Error processing Lex response:', err);
      setError('Error processing Lex response');
    }
  };
  
  // Convert AudioBuffer to PCM format
  const convertToPCM = (audioBuffer: AudioBuffer, targetSampleRate: number): ArrayBuffer => {
    // Get the audio data
    const originalSampleRate = audioBuffer.sampleRate;
    const originalData = audioBuffer.getChannelData(0); // Get mono channel
    
    console.log('LexDebugger: Converting from', originalSampleRate, 'Hz to', targetSampleRate, 'Hz');
    console.log('LexDebugger: Original data length:', originalData.length, 'samples');
    
    // Calculate the new length based on the target sample rate
    const targetLength = Math.round(originalData.length * targetSampleRate / originalSampleRate);
    console.log('LexDebugger: Target length:', targetLength, 'samples');
    
    // Create a new buffer for the resampled data
    const resampledData = new Float32Array(targetLength);
    
    // Improved resampling algorithm with linear interpolation
    const step = originalSampleRate / targetSampleRate;
    for (let i = 0; i < targetLength; i++) {
      const exactPosition = i * step;
      const position1 = Math.floor(exactPosition);
      const position2 = Math.min(position1 + 1, originalData.length - 1);
      const fraction = exactPosition - position1;
      
      // Linear interpolation between the two closest samples
      resampledData[i] = (1 - fraction) * originalData[position1] + fraction * originalData[position2];
    }
    
    // Apply a simple low-pass filter to reduce aliasing
    const filterSize = 5;
    const filtered = new Float32Array(resampledData.length);
    for (let i = 0; i < resampledData.length; i++) {
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - filterSize); j <= Math.min(resampledData.length - 1, i + filterSize); j++) {
        sum += resampledData[j];
        count++;
      }
      filtered[i] = sum / count;
    }
    
    // Normalize the audio to use the full dynamic range
    let maxSample = 0;
    for (let i = 0; i < filtered.length; i++) {
      maxSample = Math.max(maxSample, Math.abs(filtered[i]));
    }
    
    const normalizationFactor = maxSample > 0 ? 0.9 / maxSample : 1;
    console.log('LexDebugger: Normalization factor:', normalizationFactor);
    
    // Convert to 16-bit PCM
    const pcmData = new Int16Array(filtered.length);
    for (let i = 0; i < filtered.length; i++) {
      // Convert float to 16-bit signed integer with normalization
      pcmData[i] = Math.max(-32768, Math.min(32767, Math.round(filtered[i] * normalizationFactor * 32767)));
    }
    
    console.log('LexDebugger: PCM data created, length:', pcmData.length, 'samples');
    
    return pcmData.buffer;
  };
  
  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Lex Voice Integration Debugger</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          style={{
            padding: '10px 20px',
            backgroundColor: isRecording ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
          disabled={isProcessing}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
        
        <button
          onClick={sendToLex}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          disabled={!audioBlob || isProcessing || isRecording}
        >
          {isProcessing ? 'Processing...' : 'Send to Lex'}
        </button>
      </div>
      
      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}
      
      {audioUrl && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Recorded Audio</h3>
          <audio controls src={audioUrl} style={{ width: '100%' }}></audio>
          <p>
            <strong>Size:</strong> {audioBlob?.size} bytes | 
            <strong> Type:</strong> {audioBlob?.type}
          </p>
        </div>
      )}
      
      {extractedTranscript && (
        <div style={{ 
          padding: '15px',
          backgroundColor: '#d4edda',
          color: '#155724',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h3>Extracted Transcript</h3>
          <p style={{ fontSize: '18px', fontWeight: 'bold' }}>{extractedTranscript}</p>
        </div>
      )}
      
      {lexResponse && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Lex Response</h3>
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '15px',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            overflowX: 'auto'
          }}>
            <h4>Response Structure</h4>
            <ul>
              {Object.keys(lexResponse).map(key => (
                <li key={key}><strong>{key}</strong>: {typeof lexResponse[key]}</li>
              ))}
            </ul>
            
            <h4>Decoded Data</h4>
            {Object.keys(decodedData).length > 0 ? (
              <div>
                {Object.entries(decodedData).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: '10px' }}>
                    <h5>{key}</h5>
                    <pre style={{ 
                      backgroundColor: '#e9ecef',
                      padding: '10px',
                      borderRadius: '4px',
                      overflowX: 'auto',
                      maxHeight: '200px'
                    }}>
                      {value !== null ? value : 'Failed to decode'}
                    </pre>
                  </div>
                ))}
              </div>
            ) : (
              <p>No decoded data available</p>
            )}
            
            <h4>Raw Response</h4>
            <pre style={{ 
              backgroundColor: '#e9ecef',
              padding: '10px',
              borderRadius: '4px',
              overflowX: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(lexResponse, null, 2)}
            </pre>
          </div>
        </div>
      )}
      
      <div style={{ marginTop: '30px' }}>
        <h3>AWS Configuration</h3>
        <pre style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '15px',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
{`Region: ${region}
Identity Pool ID: ${identityPoolId}
Bot ID: ${botId}
Bot Alias ID: ${botAliasId}
Locale ID: ${localeId}`}
        </pre>
      </div>
    </div>
  );
};

export default LexDebugger;